import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEllipsisH, faEye, faHome, faPlus, faSearch, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, ButtonGroup, Card, Col, Dropdown, Form, InputGroup, Pagination, Row, Table } from "@themesberg/react-bootstrap";
import { useHistory } from "react-router-dom";

import { deleteProduct, getProducts } from "../api/products.api";
import ConfirmDialog from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";

const PAGE_SIZE = 10;

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString();
};

const ProductTableSkeleton = () => (
  <>
    {[...Array(PAGE_SIZE)].map((_, index) => (
      <tr key={index}>
        <td><div className="skeleton-text" style={{ width: "20px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "120px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "48px", height: "48px", borderRadius: "0.5rem" }} /></td>
        <td><div className="skeleton-text" style={{ width: "150px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "40px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "50px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "100px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "30px", height: "20px" }} /></td>
      </tr>
    ))}
  </>
);

export default () => {
  const history = useHistory();
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [actionProductId, setActionProductId] = useState(null);
  const [pendingDeleteProduct, setPendingDeleteProduct] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getProducts({ page, limit: PAGE_SIZE, search: searchQuery });
      setProducts(response.data || []);
      setPagination(response.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } catch (error) {
      showToast({ message: error.message || "Failed to load products", variant: "danger" });
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, showToast]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSearchQuery(searchInput);
  };

  const handleDeleteProduct = async () => {
    if (!pendingDeleteProduct) return;
    const productId = pendingDeleteProduct.id;
    setActionProductId(productId);

    try {
      const response = await deleteProduct(productId);
      showToast({ message: response.message || "Product deleted successfully.", variant: "success" });
      await loadProducts();
    } catch (error) {
      showToast({ message: error.message || "Failed to delete product", variant: "danger" });
    } finally {
      setActionProductId(null);
      setPendingDeleteProduct(null);
    }
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item active>Products</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Products</h4>
          <p className="mb-0">Manage products, variants, and inventory from one place.</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button variant="primary" onClick={() => history.push(Routes.ProductCreate.path)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Product
          </Button>
        </div>
      </div>

      <div className="table-settings mb-4">
        <Row className="justify-content-between align-items-center">
          <Col xs={12} md={6} lg={4}>
            <Form onSubmit={handleSearchSubmit}>
              <InputGroup>
                <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search products"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
                <Button type="submit" variant="primary">Search</Button>
              </InputGroup>
            </Form>
          </Col>
        </Row>
      </div>
      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="pt-0">
          <Table hover className="user-table align-items-center">
            <thead>
              <tr>
                <th className="border-bottom">#</th>
                <th className="border-bottom">Name</th>
                <th className="border-bottom">Image</th>
                <th className="border-bottom">Description</th>
                <th className="border-bottom">Variants</th>
                <th className="border-bottom">Total Stock</th>
                <th className="border-bottom">Created</th>
                <th className="border-bottom">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <ProductTableSkeleton />
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-5">No products found.</td>
                </tr>
              ) : (
                products.map((product, index) => (
                  <tr key={product.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{product.name}</td>
                    <td>
                      {product.image_url ? (
                        <img
                          src={resolveAssetUrl(product.image_url)}
                          alt={product.name}
                          style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "0.5rem" }}
                        />
                      ) : "N/A"}
                    </td>
                    <td>{product.description || "N/A"}</td>
                    <td>{product.variant_count}</td>
                    <td>{product.total_stock}</td>
                    <td>{formatDate(product.created_at)}</td>
                    <td>
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle as={Button} split variant="link" className="text-dark m-0 p-0">
                          <span className="icon icon-sm">
                            <FontAwesomeIcon icon={faEllipsisH} className="icon-dark" />
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => history.push(`${Routes.Products.path}/${product.id}`)}>
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Details
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => history.push(`${Routes.Products.path}/${product.id}/edit`)}>
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => setPendingDeleteProduct(product)}
                            disabled={actionProductId === product.id}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <Card.Footer className="px-3 border-0">
            <div className="d-lg-flex align-items-center justify-content-between">
              <Pagination className="mb-2 mb-lg-0">
                <Pagination.Prev disabled={loading || pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>Previous</Pagination.Prev>
                <Pagination.Item active>{pagination.page}</Pagination.Item>
                <Pagination.Next disabled={loading || pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next</Pagination.Next>
              </Pagination>
              <small className="fw-bold">
                Showing <b>{products.length}</b> of <b>{pagination.total}</b> products
              </small>
            </div>
          </Card.Footer>
        </Card.Body>
      </Card>
      <ConfirmDialog
        show={Boolean(pendingDeleteProduct)}
        title={pendingDeleteProduct ? `Delete ${pendingDeleteProduct.name}` : "Delete Product"}
        body="This will delete the product and its variants. Do you want to continue?"
        confirmLabel="Delete Product"
        confirmVariant="danger"
        processing={Boolean(pendingDeleteProduct) && actionProductId === pendingDeleteProduct?.id}
        onCancel={() => setPendingDeleteProduct(null)}
        onConfirm={handleDeleteProduct}
      />
    </>
  );
};
